interface Props { onClose: () => void; }

const MOD_ROWS = [
  ["contains",          "(?i).*value.*",          "Case-insensitive substring match"],
  ["endswith",          "(?i).*value$",            "Case-insensitive suffix match"],
  ["startswith",        "(?i)^value.*",            "Case-insensitive prefix match"],
  ["re",                "value (as-is)",           "Raw PCRE2 pattern, passed through unchanged"],
  ["contains|all",      "(?i).*a.*(?=.*b.*)",      "AND: all values must appear in the field"],
  ["base64offset",      "(?:QWxp|QVhp|...)",       "Base64-encoded value with 3 byte-offset variants"],
  ["windash",           "(?:-|/|–|—)",             "Windows dash variants: -, /, en-dash, em-dash"],
  ["wide",              "v\\x00a\\x00l\\x00",      "UTF-16LE wide-char encoding (Windows paths)"],
  ["1 of selection_*",  "OR across sub-selections","Any one wildcard selection match is enough"],
  ["all of selection_*","AND across sub-selections","All wildcard selections must match"],
];

const COND_ROWS = [
  ["selection_a and selection_b",  "Both field sets must match"],
  ["selection_a or selection_b",   "Either field set matches"],
  ["not selection_a",              "Field must NOT match"],
  ["selection and not filter",     "Match selection while excluding filter set"],
  ["1 of selection_*",             "At least one wildcard selection matches"],
  ["all of selection_*",           "All wildcard selections must match"],
];

const LEVEL_ROWS = [
  ["informational", "5"],
  ["low",           "7"],
  ["medium",        "10"],
  ["high",          "12"],
  ["critical",      "15"],
];

const SID_ROWS = [
  ["windows / process_creation",  "61603"],
  ["windows / security",          "18100"],
  ["windows / system",            "18101"],
  ["windows / powershell",        "91800"],
  ["windows / sysmon",            "61600"],
  ["linux / syslog",              "5104"],
  ["linux / auditd",              "80700"],
  ["macos / process_creation",    "100010"],
  ["aws / cloudtrail",            "80300"],
  ["azure / activity",            "81200"],
  ["gcp / gcp.audit",             "81300"],
  ["m365 / exchange",             "91550"],
  ["network / *",                 "7101"],
];

const STATUS_ROWS = [
  ["stable",       "Production-ready, thoroughly reviewed"],
  ["test",         "Validated in real environments, may need tuning"],
  ["experimental", "Untested, elevated false-positive risk"],
  ["deprecated",   "Superseded by another rule, do not use"],
  ["unsupported",  "Cannot be converted by this backend"],
];

const OUTPUT_ROWS = [
  ["<if_sid>",              "Parent SID anchor, logsource detection chain"],
  ["<field type='pcre2'>",  "PCRE2 pattern match, field condition from detection"],
  ["<description>",         "Rule title from Sigma title: field"],
  ["<group>",               "Comma-list: product, category, sigma level, technique"],
  ["<mitre><id>",           "ATT&CK technique IDs from attack.t* tags"],
  ["<options>no_full_log",  "Suppress full event log (toggle in Settings)"],
  ["level=",                "Wazuh severity mapped from Sigma level (configurable)"],
  ["id=",                   "Rule ID from ruleIdStart, auto-incremented per rule"],
];

function RefTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={i} style={{
              padding: "5px 8px", textAlign: "left", fontWeight: 600, fontSize: 10,
              color: "var(--t3)", borderBottom: "1px solid var(--bd)",
              textTransform: "uppercase", letterSpacing: "0.06em",
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} style={{ borderBottom: "1px solid rgba(var(--accent-rgb),.05)" }}>
            {row.map((cell, ci) => (
              <td key={ci} style={{
                padding: "6px 8px",
                color: ci === 0 ? "var(--accent)" : ci === 1 ? "var(--t1)" : "var(--t3)",
                fontFamily: ci <= 1 ? "'JetBrains Mono', monospace" : "inherit",
                fontSize: 11,
              }}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Section({ title }: { title: string }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
      color: "var(--accent)", borderBottom: "1px solid var(--bd)", paddingBottom: 6,
      marginTop: 22, marginBottom: 10,
    }}>{title}</div>
  );
}

export function SigmaReference({ onClose }: Props) {
  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", justifyContent: "flex-end" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
      />
      <div style={{
        position: "relative", zIndex: 1,
        width: 540, height: "100%", overflowY: "auto",
        background: "var(--s0)", borderLeft: "1px solid var(--bd)",
        padding: "24px 22px",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--t1)" }}>Sigma Quick Reference</span>
          <button
            onClick={onClose}
            style={{
              background: "transparent", border: "1px solid var(--bd)", borderRadius: 6,
              color: "var(--t3)", cursor: "pointer", padding: "4px 10px", fontSize: 11,
              fontFamily: "var(--font-ui)",
            }}
          >✕ Close</button>
        </div>
        <div style={{ fontSize: 11, color: "var(--t3)", marginBottom: 4 }}>
          Field modifiers, condition syntax, level mappings, logsource SIDs, status values, and Wazuh output elements.
        </div>

        <Section title="Field Modifiers" />
        <RefTable
          headers={["Modifier", "PCRE2 Pattern", "Meaning"]}
          rows={MOD_ROWS}
        />

        <Section title="Condition Operators" />
        <RefTable
          headers={["Condition", "Behaviour"]}
          rows={COND_ROWS}
        />

        <Section title="Sigma Level → Wazuh Level (defaults)" />
        <RefTable
          headers={["Sigma", "Wazuh"]}
          rows={LEVEL_ROWS}
        />
        <div style={{ fontSize: 10, color: "var(--t3)", marginTop: 6 }}>
          Configurable per session in Settings → Severity Levels.
        </div>

        <Section title="Common Logsources → Parent SID (if_sid)" />
        <RefTable
          headers={["Logsource", "if_sid"]}
          rows={SID_ROWS}
        />
        <div style={{ fontSize: 10, color: "var(--t3)", marginTop: 6 }}>
          Override per product in Settings → Parent Rule Overrides.
        </div>

        <Section title="Sigma Status Values" />
        <RefTable
          headers={["Status", "Meaning"]}
          rows={STATUS_ROWS}
        />
        <div style={{ fontSize: 10, color: "var(--t3)", marginTop: 6 }}>
          Filter by status in Settings → Excluded Statuses (e.g. skip experimental rules).
        </div>

        <Section title="Wazuh XML Output Elements" />
        <RefTable
          headers={["Element", "Purpose"]}
          rows={OUTPUT_ROWS}
        />
        <div style={{ fontSize: 10, color: "var(--t3)", marginTop: 6 }}>
          SigWaz generates schema-valid Wazuh XML. All output passes lxml validation before download.
        </div>

        <div style={{ flex: 1 }} />
      </div>
    </div>
  );
}
